import { useRef } from 'react';
import { isEqual } from 'lodash-es';

/**
 * Custom React hook that memoizes a complex value (like an array or object)
 * and only returns a new reference when the value has changed deeply.
 * This is useful as a dependency for other hooks like `useEffect` or `useMemo`
 * to prevent them from re-running on every render due to unstable object/array references.
 *
 * @template T The type of the value to be memoized.
 * @param {T} value The value to be memoized.
 * @returns {T} The memoized value, which is referentially stable as long as it remains deeply
 * equal to its previous value.
 */
export function useDeepCompareMemoize<T>(value: T): T {
    // `useRef` is used to store the previous value across renders without causing a re-render.
    // We type the ref to hold a value of type T, but it starts as undefined.
    const ref = useRef<T>();

    // Perform a deep comparison between the new value from the current render
    // and the stored value from the previous render.
    if (!isEqual(value, ref.current)) {
        // If they are not deeply equal, update the stored value with the current one.
        ref.current = value;
    }

    // Always return the stored value from the ref. The reference of `ref.current`
    // itself will only change when the deep comparison detects a change, making it
    // a stable dependency for other hooks.
    //
    // The `!` (Non-null Assertion Operator) tells TypeScript that we are certain
    // `ref.current` will not be null or undefined at this point. This is safe because
    // on the first render, `ref.current` is undefined, the `isEqual` check fails,
    // and `ref.current` is assigned the `value` before this return statement is reached.
    return ref.current!;
}
